import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import Iconify from '../iconify';
import { usePWAInstall } from '../../hooks/use-pwa-install';

export default function PWAInstallPrompt() {
  const theme = useTheme();
  const { isInstallable, isIOS, isStandalone, showPrompt, handleInstallClick, handleClose } =
    usePWAInstall();
  const [showInstructions, setShowInstructions] = useState(false);

  if (isStandalone || !showPrompt) {
    return null;
  }

  const handleOpenInstructions = () => {
    setShowInstructions(true);
  };

  const handleCloseInstructions = () => {
    setShowInstructions(false);
    handleClose();
  };

  // Não mostra o banner quando o modal está aberto
  const showBanner = !showInstructions;

  // Banner compacto na parte inferior
  return (
    <>
      {showBanner && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.snackbar,
            p: { xs: 1.5, sm: 2 },
            pb: { xs: 2, sm: 2 }, // Padding extra na parte inferior
            backgroundColor: theme.palette.background.paper,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            boxShadow: theme.customShadows.dialog,
          }}
        >
        <Stack spacing={1.5}>
          {/* Linha 1: Ícone, Texto e Botão Fechar */}
          <Stack direction="row" alignItems="flex-start" spacing={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                backgroundColor: theme.palette.primary.main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Iconify icon="solar:smartphone-2-bold" width={24} color="white" />
            </Box>

            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                Adicionar à Tela Inicial
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem' }}>
                Acesse o app rapidamente
              </Typography>
            </Box>

            <IconButton size="small" onClick={handleClose} sx={{ mt: -0.5 }}>
              <Iconify icon="solar:close-circle-bold" width={20} />
            </IconButton>
          </Stack>

          {/* Linha 2: Botão de Ação */}
          {!isIOS && isInstallable ? (
            <Button
              variant="contained"
              size="medium"
              fullWidth
              onClick={handleInstallClick}
              startIcon={<Iconify icon="solar:download-minimalistic-bold" />}
            >
              Instalar
            </Button>
          ) : (
            <Button
              variant="contained"
              size="medium"
              fullWidth
              onClick={handleOpenInstructions}
              startIcon={<Iconify icon="solar:add-circle-bold" />}
            >
              Como Fazer
            </Button>
          )}
        </Stack>
        </Paper>
      )}

      {/* Dialog com instruções para iOS */}
      <Dialog
        open={showInstructions}
        onClose={handleCloseInstructions}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: { 
            borderRadius: 2,
            m: 2, // Margem nas laterais
            maxHeight: 'calc(100vh - 80px)', // Deixa espaço em cima e embaixo
          },
        }}
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'flex-start', // Alinha no topo
            pt: 5, // Padding top para não ficar colado no topo
          },
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                backgroundColor: theme.palette.primary.lighter,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Iconify icon="solar:smartphone-2-bold" width={24} color="primary.main" />
            </Box>
            <Typography variant="h6">Adicionar à Tela Inicial</Typography>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pb: 2 }}>
          <Stack spacing={3}>
            <Typography variant="body2" color="text.secondary">
              Siga os passos abaixo para adicionar este site à sua tela inicial e acessá-lo como um
              aplicativo:
            </Typography>

            {isIOS ? (
              // Instruções para iOS/Safari
              <Stack spacing={2}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.primary.lighter,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      mt: 0.5,
                    }}
                  >
                    <Typography variant="subtitle2" color="primary.main">
                      1
                    </Typography>
                  </Box>
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      <Typography variant="subtitle2">Toque no botão Compartilhar</Typography>
                      <Iconify
                        icon="solar:share-bold"
                        width={20}
                        color="primary.main"
                        sx={{
                          backgroundColor: theme.palette.primary.lighter,
                          borderRadius: 0.5,
                          p: 0.5,
                        }}
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      Localizado na barra inferior do Safari
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.primary.lighter,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      mt: 0.5,
                    }}
                  >
                    <Typography variant="subtitle2" color="primary.main">
                      2
                    </Typography>
                  </Box>
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      <Typography variant="subtitle2">
                        Selecione &ldquo;Adicionar à Tela Inicial&rdquo;
                      </Typography>
                      <Iconify
                        icon="solar:add-square-bold"
                        width={20}
                        color="primary.main"
                        sx={{
                          backgroundColor: theme.palette.primary.lighter,
                          borderRadius: 0.5,
                          p: 0.5,
                        }}
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      Role para baixo no menu e encontre esta opção
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.primary.lighter,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      mt: 0.5,
                    }}
                  >
                    <Typography variant="subtitle2" color="primary.main">
                      3
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Confirme tocando em &ldquo;Adicionar&rdquo;
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      O ícone aparecerá na sua tela inicial
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            ) : (
              // Instruções para Android/Chrome
              <Stack spacing={2}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.primary.lighter,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      mt: 0.5,
                    }}
                  >
                    <Typography variant="subtitle2" color="primary.main">
                      1
                    </Typography>
                  </Box>
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      <Typography variant="subtitle2">Abra o menu do navegador</Typography>
                      <Iconify
                        icon="solar:menu-dots-bold"
                        width={20}
                        color="primary.main"
                        sx={{
                          backgroundColor: theme.palette.primary.lighter,
                          borderRadius: 0.5,
                          p: 0.5,
                        }}
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      Toque nos três pontos no canto superior direito
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.primary.lighter,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      mt: 0.5,
                    }}
                  >
                    <Typography variant="subtitle2" color="primary.main">
                      2
                    </Typography>
                  </Box>
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      <Typography variant="subtitle2">
                        Selecione &ldquo;Adicionar à tela inicial&rdquo;
                      </Typography>
                      <Iconify
                        icon="solar:add-square-bold"
                        width={20}
                        color="primary.main"
                        sx={{
                          backgroundColor: theme.palette.primary.lighter,
                          borderRadius: 0.5,
                          p: 0.5,
                        }}
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      Ou &ldquo;Instalar aplicativo&rdquo; se disponível
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.primary.lighter,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      mt: 0.5,
                    }}
                  >
                    <Typography variant="subtitle2" color="primary.main">
                      3
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Confirme a instalação
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      O ícone aparecerá na sua tela inicial
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            )}

            <Box
              sx={{
                p: 2,
                borderRadius: 1.5,
                backgroundColor: theme.palette.background.neutral,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Iconify
                  icon="solar:info-circle-bold"
                  width={20}
                  color="info.main"
                  sx={{ mt: 0.2 }}
                />
                <Typography variant="caption" color="text.secondary">
                  Após adicionar, você poderá acessar o site diretamente da sua tela inicial, como
                  se fosse um aplicativo nativo!
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
          <Button 
            variant="contained" 
            onClick={handleCloseInstructions} 
            fullWidth
            size="large"
          >
            Entendi
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
